import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Recycling, Group, TrendingUp, Nature } from '@mui/icons-material';

const ImpactStats = ({ variant = "default" }) => {
    const stats = [
        {
            icon: <Recycling />,
            value: '25K+',
            label: 'Items Classified',
            description: 'And growing every day',
            gradient: 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
            shadowColor: 'rgba(67, 160, 71, 0.3)'
        },
        {
            icon: <Group />,
            value: '1.2K+',
            label: 'Active Users',
            description: 'Join our eco community',
            gradient: 'linear-gradient(135deg, #00b894 0%, #00a085 100%)',
            shadowColor: 'rgba(0, 184, 148, 0.3)'
        },
        {
            icon: <TrendingUp />,
            value: '95%',
            label: 'Accuracy Rate',
            description: 'AI-powered precision',
            gradient: 'linear-gradient(135deg, #ffd600 0%, #ffc400 100%)',
            shadowColor: 'rgba(255, 196, 0, 0.3)'
        },
        {
            icon: <Nature />,
            value: '50T',
            label: 'CO₂ Saved',
            description: 'Environmental impact',
            gradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
            shadowColor: 'rgba(46, 125, 50, 0.3)'
        }
    ];

    const getContainerStyles = () => {
        switch (variant) {
            case 'hero':
                return {
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5fbf7 100%)',
                    py: { xs: 4, sm: 6, md: 8 },
                    px: { xs: 2, sm: 3 },
                    boxShadow: '0 8px 32px 0 rgba(67, 160, 71, 0.08)',
                    borderRadius: { xs: 3, sm: 4, md: 6 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: { xs: 3, sm: 4 },
                        background: 'linear-gradient(90deg, #43a047 0%, #66bb6a 50%, #4caf50 100%)',
                    }
                };
            case 'section':
                return {
                    background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 70%, #4caf50 100%)',
                    color: 'white',
                    py: { xs: 4, sm: 6, md: 8 },
                    px: { xs: 1, sm: 2 },
                    borderRadius: { xs: 3, sm: 4, md: 6 },
                    my: { xs: 3, sm: 4, md: 6 },
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 16px 40px 0 rgba(67, 160, 71, 0.25)',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: { xs: '150px', sm: '200px', md: '300px' },
                        height: { xs: '150px', sm: '200px', md: '300px' },
                        background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
                        borderRadius: '50%',
                        transform: 'translate(50%, 50%)',
                    }
                };
            default:
                return {
                    background: 'linear-gradient(135deg, #ffffff 0%, #f2f9f4 100%)',
                    py: { xs: 3, sm: 4, md: 6 },
                    px: { xs: 2, sm: 3 },
                    boxShadow: '0 8px 24px 0 rgba(67, 160, 71, 0.1)',
                    borderRadius: { xs: 3, sm: 4, md: 6 },
                    border: '1px solid rgba(67, 160, 71, 0.1)'
                };
        }
    };

    return (
        <Box sx={getContainerStyles()}>
            <Container maxWidth="lg">
                {variant === 'section' && (
                    <Box sx={{
                        position: 'relative',
                        zIndex: 2,
                        mb: { xs: 4, sm: 5, md: 6 },
                        textAlign: 'center',
                        px: { xs: 1, sm: 2 }
                    }}>
                        <Typography
                            variant="h3"
                            fontWeight={800}
                            gutterBottom
                            sx={{
                                color: 'white',
                                mb: { xs: 1.5, sm: 2 },
                                textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                letterSpacing: '-0.025em',
                                fontSize: {
                                    xs: '1.8rem',
                                    sm: '2.4rem',
                                    md: '3rem'
                                },
                                lineHeight: { xs: 1.2, sm: 1.3 }
                            }}
                        >
                            🌍 Our Environmental Impact
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontWeight: 500,
                                maxWidth: { xs: '280px', sm: '400px', md: '600px' },
                                mx: 'auto',
                                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                                fontSize: {
                                    xs: '0.9rem',
                                    sm: '1.1rem',
                                    md: '1.25rem'
                                }
                            }}
                        >
                            Together, we're building a sustainable future through AI-powered waste classification
                        </Typography>
                    </Box>
                )}

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center" alignItems="center">
                    {stats.map((stat, index) => (
                        <Grid item xs={6} sm={6} md={3} key={index}>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: { xs: 2, sm: 2.5, md: 3 },
                                    px: { xs: 1, sm: 1.5 },
                                    position: 'relative',
                                    zIndex: 2,
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        transform: { xs: 'translateY(-4px)', sm: 'translateY(-6px)', md: 'translateY(-8px)' },
                                        '& .stat-icon': {
                                            transform: 'scale(1.1) rotate(5deg)',
                                        }
                                    }
                                }}
                            >
                                <Box
                                    className="stat-icon"
                                    sx={{
                                        width: { xs: 60, sm: 75, md: 100 },
                                        height: { xs: 60, sm: 75, md: 100 },
                                        borderRadius: '50%',
                                        background: variant === 'section'
                                            ? 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)'
                                            : stat.gradient,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: { xs: 2, sm: 2.5, md: 3 },
                                        boxShadow: variant === 'section'
                                            ? '0 8px 32px rgba(255,255,255,0.2), inset 0 1px 1px rgba(255,255,255,0.3)'
                                            : `0 8px 32px ${stat.shadowColor}`,
                                        border: variant === 'section'
                                            ? '2px solid rgba(255,255,255,0.3)'
                                            : 'none',
                                        transition: 'all 0.3s ease-in-out',
                                        backdropFilter: variant === 'section' ? 'blur(10px)' : 'none'
                                    }}
                                >
                                    {React.cloneElement(stat.icon, {
                                        sx: {
                                            fontSize: { xs: 28, sm: 32, md: 42 },
                                            color: 'white',
                                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                        }
                                    })}
                                </Box>

                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    gutterBottom
                                    sx={{
                                        fontSize: {
                                            xs: '1.8rem',
                                            sm: '2.2rem',
                                            md: '3.5rem'
                                        },
                                        background: variant === 'section'
                                            ? 'linear-gradient(45deg, #ffffff 0%, rgba(255,255,255,0.8) 100%)'
                                            : 'linear-gradient(45deg, #1b5e20 0%, #43a047 50%, #2e7d32 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        textShadow: variant === 'section' ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
                                        letterSpacing: '-0.02em',
                                        mb: { xs: 0.5, sm: 0.75, md: 1 },
                                        lineHeight: { xs: 1.1, sm: 1.2 }
                                    }}
                                >
                                    {stat.value}
                                </Typography>

                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{
                                        color: variant === 'section' ? 'rgba(255,255,255,0.95)' : '#1b5e20',
                                        mb: { xs: 0.5, sm: 0.75, md: 1 },
                                        fontSize: {
                                            xs: '0.875rem',
                                            sm: '1rem',
                                            md: '1.25rem'
                                        },
                                        letterSpacing: '0.01em',
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    {stat.label}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: variant === 'section' ? 'rgba(255,255,255,0.8)' : '#546e7a',
                                        fontWeight: 500,
                                        fontSize: {
                                            xs: '0.75rem',
                                            sm: '0.875rem',
                                            md: '1rem'
                                        },
                                        lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
                                        maxWidth: { xs: '140px', sm: '160px', md: '200px' },
                                        mx: 'auto'
                                    }}
                                >
                                    {stat.description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Decorative Elements for section variant */}
                {variant === 'section' && (
                    <>
                        {/* Floating eco elements */}
                        {[...Array(6)].map((_, i) => (
                            <Box
                                key={`eco-${i}`}
                                sx={{
                                    position: 'absolute',
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                    opacity: 0.1,
                                    animation: `float-gentle ${4 + i}s infinite ease-in-out`,
                                    left: `${10 + (i * 15)}%`,
                                    top: `${15 + (i * 10)}%`,
                                    zIndex: 1,
                                    '@keyframes float-gentle': {
                                        '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                                        '50%': { transform: 'translateY(-20px) rotate(10deg)' }
                                    }
                                }}
                            >
                                {['🌱', '♻️', '🌍', '🍃', '🌿', '💚'][i]}
                            </Box>
                        ))}
                    </>
                )}
            </Container>
        </Box>
    );
};

export default ImpactStats;
